import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, Lock } from 'lucide-react';

const linkStyle = (active) => (active ? { background: 'var(--p-hover)', color: 'var(--p-text)' } : { color: 'var(--p-muted)' });
const isOn = (item, path) => item.to === '/portal' ? path === '/portal' : path === item.to || path.startsWith(item.to + '/');

const LockMark = ({ item, size = 11 }) => (item.soon || item.locked)
  ? <Lock size={size} className="text-[#F97316]" aria-hidden="true" data-testid={`portal-nav-lock-${item.key}`} /> : null;

const Group = ({ group, path }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  useEffect(() => setOpen(false), [path]);
  const active = group.children.some((c) => isOn(c, path));
  const Icon = group.icon;
  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((v) => !v)} data-testid={`portal-nav-group-${group.key}`} aria-expanded={open}
        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap hover:text-[var(--p-text)]"
        style={linkStyle(active)}>
        <Icon size={15} aria-hidden="true" /> {group.label}
        <ChevronDown size={13} className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div className="absolute left-0 mt-2 w-64 rounded-xl shadow-2xl border py-1.5 z-50 animate-in fade-in slide-in-from-top-1 duration-150"
          style={{ background: 'var(--p-surface)', borderColor: 'var(--p-border)' }} data-testid={`portal-nav-menu-${group.key}`}>
          {group.children.map((c) => (
            <Link key={c.key} to={c.to} data-testid={`portal-nav-${c.key}`}
              className="flex items-start gap-3 px-4 py-2.5 transition-colors hover:bg-[var(--p-hover)]">
              <c.icon size={16} className="mt-0.5 shrink-0" style={{ color: isOn(c, path) ? '#1D4ED8' : 'var(--p-muted)' }} aria-hidden="true" />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-1.5 text-sm font-medium" style={{ color: 'var(--p-text)' }}>{c.label} <LockMark item={c} /></span>
                {c.desc && <span className="block text-xs mt-0.5" style={{ color: 'var(--p-faint)' }}>{c.soon ? 'Coming soon' : c.desc}</span>}
              </span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export const PortalDesktopNav = ({ items }) => {
  const { pathname } = useLocation();
  return (
    <nav className="hidden lg:flex items-center gap-1 flex-1 justify-center" data-testid="portal-nav">
      {items.map((item) => item.children ? <Group key={item.key} group={item} path={pathname} /> : (
        <Link key={item.key} to={item.to} data-testid={`portal-nav-${item.key}`}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap hover:text-[var(--p-text)]"
          style={linkStyle(isOn(item, pathname))}>
          <item.icon size={15} aria-hidden="true" /> {item.label} <LockMark item={item} />
        </Link>
      ))}
    </nav>
  );
};

export const PortalMobileNav = ({ items, onNavigate }) => {
  const { pathname } = useLocation();
  const row = (item) => (
    <Link key={item.key} to={item.to} onClick={onNavigate} data-testid={`portal-mobile-nav-${item.key}`}
      className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium" style={linkStyle(isOn(item, pathname))}>
      <item.icon size={16} aria-hidden="true" /> {item.label}
      <span className="ml-auto"><LockMark item={item} size={12} /></span>
    </Link>
  );
  return (
    <nav className="lg:hidden border-t px-4 py-2 space-y-1 max-h-[70vh] overflow-y-auto" style={{ borderColor: 'var(--p-border)' }} data-testid="portal-mobile-nav">
      {items.map((item) => item.children ? (
        <div key={item.key} className="pt-2">
          <div className="px-3 pb-1 text-[11px] tracking-[0.15em] uppercase font-semibold" style={{ color: 'var(--p-faint)' }}>{item.label}</div>
          {item.children.map(row)}
        </div>
      ) : row(item))}
    </nav>
  );
};
